"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import { useRefreshSessionClaims } from "@/hooks/useRefreshSessionClaims";
import { useSearchParams } from "next/navigation";
import { toast } from "sonner";
import { sendGAEventSafe } from "@/lib/ga";
import { toasterReady } from "@/lib/toasterReady";
import { hasPurchaseEventSent, markPurchaseEventSent } from "@/lib/purchaseDedup";
import {
  PASSES,
  isPassProductId,
  passTypeForProductId,
} from "@/types/pass";
import { trackPurchase } from "@/lib/metaPixel";
import PdfDownloadGuideModal from "@/components/PdfDownloadGuideModal";
import TrialActivatedModal from "@/components/TrialActivatedModal";

const subscribeNoop = () => () => {};
const getClientMountedSnapshot = () => true;
const getServerMountedSnapshot = () => false;

export default function PaymentSuccessHandler() {
  const mounted = useSyncExternalStore(
    subscribeNoop,
    getClientMountedSnapshot,
    getServerMountedSnapshot,
  );

  if (!mounted) {
    return null;
  }

  return <PaymentSuccessHandlerContent />;
}

function PaymentSuccessHandlerContent() {
  const searchParams = useSearchParams();
  const [showPdfGuideModal, setShowPdfGuideModal] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);

  // PPR 페이지(홈 등)에서는 정적 셸 hydration 후 동적 스트림 도착 시 라우터 상태가
  // 갱신되며 useSearchParams()가 "값은 같지만 참조가 다른" 객체를 반환한다. 객체를
  // effect 의존성으로 쓰면 같은 결제 리다이렉트에 effect가 2번 실행되어 GA4/Meta
  // 구매 전환이 이중 집계되고 토스트가 중복 발화된다 → 문자열로 비교해 차단.
  const searchParamsString = searchParams.toString();
  const refreshSessionClaims = useRefreshSessionClaims();

  useEffect(() => {
    const params = new URLSearchParams(searchParamsString);
    if (params.get("success") === "true" && params.get("paymentProvider") === "toss") {
      // Toss의 전체 페이지 복귀는 usePaymentPolling을 거치지 않는다.
      void refreshSessionClaims();
    }
  }, [searchParamsString, refreshSessionClaims]);

  useEffect(() => {
    let isActive = true;

    const showPaymentToast = (showToast: () => void) => {
      if (typeof window === "undefined") {
        return;
      }

      void toasterReady.then(() => {
        if (isActive) {
          showToast();
        }
      });
    };

    const params = new URLSearchParams(searchParamsString);
    const openPurchases = params.get('openPurchases');
    const success = params.get('success');
    const orderId = params.get('orderId');
    const amount = params.get('amount');
    const productId = params.get('productId');
    const productName = params.get('productName');
    const paymentError = params.get('paymentError');

    // 결제 실패 처리
    if (paymentError) {
      showPaymentToast(() => {
        toast.error(`결제 실패: ${paymentError}`, {
          // 같은 id 재발화는 새 토스트 생성 대신 기존 토스트 갱신 (중복 방어)
          id: `payment-error-${orderId ?? "unknown"}`,
          duration: 5000,
        });
      });
      return () => {
        isActive = false;
      };
    }

    if (openPurchases === 'true' && success === 'true') {
      // 이용권 상태는 JWT publicMetadata 직접 조회로 통일됨 (JCG-2017). 결제 후
      // metadata 갱신은 usePaymentPolling 또는 위의 토스 복귀 effect가 처리한다.

      // dev 모드에서는 GA/Meta Pixel 구매 이벤트를 전송하지 않음 (수익으로 잡히지 않도록)
      const isDev = process.env.NODE_ENV === 'development';

      if (!isDev) {
        const purchaseValue = amount ? Number(amount) : 4000;
        const shouldSendGa4Purchase =
          !orderId || !hasPurchaseEventSent('ga4', orderId);
        const shouldSendMetaPurchase =
          !orderId || !hasPurchaseEventSent('meta', orderId);

        // GA4 구매 이벤트 전송 (프로덕션에서만)
        if (shouldSendGa4Purchase) {
          sendGAEventSafe('event', 'manual_event_PURCHASE', {
            transaction_id: orderId || 'unknown',
            value: purchaseValue, // 결제 금액을 전환 값으로 사용
            currency: 'KRW', // 통화 설정
            items: [
              {
                item_id: productId || 'unknown',
                item_name: productName || 'Unknown Product',
                price: purchaseValue,
                quantity: 1
              }
            ]
          });
          if (orderId) {
            markPurchaseEventSent('ga4', orderId);
          }
        }

        // Meta Pixel 구매 이벤트 전송 (프로덕션에서만)
        if (shouldSendMetaPurchase) {
          const sentMetaPurchase = trackPurchase({
            value: purchaseValue,
            currency: 'KRW',
            content_name: productName || 'Unknown Product',
            content_ids: productId ? [productId] : undefined,
            content_type: 'product',
            num_items: 1,
          });
          if (orderId && sentMetaPurchase) {
            markPurchaseEventSent('meta', orderId);
          }
        }
      } else {
        console.log('[DEV] GA4/Meta Pixel 구매 이벤트 전송 생략 (dev 모드)');
      }

      // 결제 완료 토스트 알림 표시
      // 이용권 구매인지 확인
      let toastMessage: string;
      let shouldShowPdfGuide = false;

      const purchasedPassType = productId
        ? passTypeForProductId(productId)
        : null;

      if (purchasedPassType === "starter") {
        toastMessage = `${PASSES.starter.name} 이용권 적용완료!`;
      } else if (purchasedPassType === "premium") {
        toastMessage = `${PASSES.premium.name} 이용권 적용완료!`;
      } else if (purchasedPassType === "complete") {
        toastMessage = `${PASSES.complete.name} 이용권 적용완료!`;
        shouldShowPdfGuide = true; // Complete 이용권은 요약 PDF 포함
      } else {
        // PDF 구매인 경우
        toastMessage = `PDF 구매 완료! 구매 목록에서 다운로드하세요.`;
        shouldShowPdfGuide = true;
      }

      // 결제 후 window.location 전체 새로고침으로 진입하면 이 useEffect가
      // <SonnerToaster>의 구독(useEffect) 전에 실행될 수 있다. Sonner는 마운트 전에
      // 쌓인 toast를 replay하지 않으므로 Toaster 준비 신호 이후에 발화시킨다.
      showPaymentToast(() => {
        toast.success(toastMessage, {
          // 같은 id 재발화는 새 토스트 생성 대신 기존 토스트 갱신 (중복 방어)
          id: `payment-success-${orderId ?? "unknown"}`,
          duration: 5000,
        });
      });

      // PDF 구매로 무료체험 활성화된 경우 모달 표시
      const trialActivated = params.get('trialActivated');
      if (trialActivated === 'true') {
        setTimeout(() => {
          setShowTrialModal(true);
        }, 500);
        // TrialBanner 중복 토스트 방지
        localStorage.setItem('trialToastShown', 'true');
      }

      // PDF 다운로드 안내 모달 표시 (체험 모달이 없을 때만 바로 표시)
      if (shouldShowPdfGuide && trialActivated !== 'true') {
        setTimeout(() => {
          setShowPdfGuideModal(true);
        }, 500);
      }
    }

    return () => {
      isActive = false;
    };
  }, [searchParamsString]);

  const handleTrialModalClose = () => {
    setShowTrialModal(false);
    // 체험 모달 닫힌 후 PDF 안내 모달 표시
    const productId = searchParams.get('productId');
    if (productId && !isPassProductId(productId)) {
      setTimeout(() => {
        setShowPdfGuideModal(true);
      }, 300);
    }
  };

  return (
    <>
      <TrialActivatedModal
        isOpen={showTrialModal}
        onClose={handleTrialModalClose}
      />
      <PdfDownloadGuideModal
        isOpen={showPdfGuideModal}
        onClose={() => setShowPdfGuideModal(false)}
      />
    </>
  );
}
